import { useSyncExternalStore } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import {
  accessFromToken,
  createAuthClient,
  createTokenSession,
  webStore,
  type Access,
  type AuthClient,
  type TokenResponse,
  type TokenSession,
} from './api';
import { BASE_URL, session as hubSession, useHubLogin } from '../session';
import { SCOPES } from '../api';

/**
 * M8: AUTH administration of the AUTH environments ("targets") the hub is configured for.
 *
 * The hub signs in to every target at hub sign-in and hands the token pairs to the browser (`storeTargetLogins`);
 * each target keeps its own token session (`oph:t:<id>` in sessionStorage, refreshed through
 * `/auth-targets/<id>/refresh`). Requests go through the hub proxy with the hub token as `Authorization` and the
 * target token as `X-Target-Token`. The pages ported from auth-admin use `client` and `useLogin()` exactly as they
 * did there; both follow the target selected in the URL (`?target=`).
 */

export interface AuthTargetInfo {
  id: string;
  label: string;
  authUrl: string;
  scimUrl: string | null;
  /** nyce.buddy of this environment (M12); null = none configured. */
  buddyUrl?: string | null;
  /** NYCE Design of this environment (M23); null = none configured. */
  designUrl?: string | null;
  /** The hub can administer this environment's print queue (PrintQueueUrl and the hub's token configured). */
  printQueue?: boolean;
  /** The hub can administer this environment's automation gateway (AutomationGwUrl and the hub's admin key). */
  automationGw?: boolean;
  /** Federated sign-in to this environment (its AUTH `/authorize`); null = password only. */
  sso?: { clientId: string; tenant: string } | null;
  /** The hub's own AUTH and operator tenant: the hub session is this environment's session (no sign-in of its own). */
  sharesHubSession?: boolean;
}

/** One entry of the login response's `targets`: a token pair, or why that environment could not sign in. */
export type TargetLogin = { id: string } & (Partial<TokenResponse> & { error?: string; code?: string });

const store = webStore(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
const LAST = 'oph:target';
const listeners = new Set<() => void>();
let version = 0;
const notify = () => {
  version++;
  listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

// ------------------------------------------------------------------------------------------------ transport

/** fetch with the hub token; a hub 401 (not a target one) refreshes the hub session and retries once. */
export async function hubFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const send = async (token: string | null) => {
    const h = new Headers(init.headers);
    if (token) h.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers: h });
  };
  const first = await send(await hubSession.getToken());
  if (first.status !== 401) return first;
  const text = await first.text();
  const replay = () => new Response(text, { status: 401, headers: first.headers });
  if (text.includes('"target_unauthorized"')) return replay();
  const fresh = await hubSession.refresh().catch(() => null);
  return fresh ? send(fresh) : replay();
}

/** The AUTH client puts the target token in `Authorization`; the hub wants it in `X-Target-Token`. */
export const proxyFetch: typeof fetch = (input, init = {}) => {
  const h = new Headers(init.headers);
  const auth = h.get('Authorization');
  h.delete('Authorization');
  if (auth?.startsWith('Bearer ')) h.set('X-Target-Token', auth.slice(7));
  return hubFetch(String(input), { ...init, headers: h });
};

// ------------------------------------------------------------------------------------------- target sessions

const sessions = new Map<string, TokenSession>();
const views = new Map<string, TokenSession>();
const clients = new Map<string, { auth: AuthClient; scim: AuthClient }>();

const sharesHubSession = (id: string) => !!targets.find((t) => t.id === id)?.sharesHubSession;

/**
 * The session of environment `id`. An environment on the hub's own AUTH and operator tenant reads the hub session (its
 * token is valid there too; one refresh chain, refreshed by the hub session); signing in or out there is the hub's.
 * Decided on every call, so clients created before the target list loaded follow it.
 */
export function targetSession(id: string): TokenSession {
  const cached = views.get(id);
  if (cached) return cached;
  const pick = () => (sharesHubSession(id) ? hubSession : ownSession(id));
  const own = () => (sharesHubSession(id) ? null : ownSession(id));
  const view: TokenSession = {
    getToken: async () => pick().getToken(), // the hub session may answer synchronously
    refresh: () => pick().refresh(),
    peek: () => pick().peek(),
    refreshToken: () => pick().refreshToken(),
    isSignedIn: () => pick().isSignedIn(),
    msUntilRefresh: () => pick().msUntilRefresh(),
    setTokens: (t) => own()?.setTokens(t),
    clear: () => own()?.clear(),
    startAutoRefresh: () => own()?.startAutoRefresh() ?? (() => {}),
  };
  views.set(id, view);
  return view;
}

function ownSession(id: string): TokenSession {
  let s = sessions.get(id);
  if (!s) {
    s = createTokenSession({
      baseUrl: `${BASE_URL}/auth-targets/${encodeURIComponent(id)}`,
      store,
      prefix: `oph:t:${id}`,
      fetch: (input, init) => hubFetch(String(input), init),
      onExpired: () => notify(),
      onRefreshed: () => notify(),
    });
    sessions.set(id, s);
  }
  return s;
}

function clientsOf(id: string) {
  let c = clients.get(id);
  if (!c) {
    const auth = targetSession(id);
    c = {
      auth: createAuthClient({
        baseUrl: `${BASE_URL}/auth-admin/${encodeURIComponent(id)}`,
        auth,
        fetch: proxyFetch,
      }),
      scim: createAuthClient({
        baseUrl: `${BASE_URL}/scim-admin/${encodeURIComponent(id)}`,
        auth,
        fetch: proxyFetch,
      }),
    };
    clients.set(id, c);
  }
  return c;
}

/** The AUTH admin client of a given environment (dashboards read several at once). */
export const authClientFor = (id: string): AuthClient => clientsOf(id).auth;

/** SCIM gateway admin of a given environment. */
export const scimFor = (id: string) => ({
  list: () => clientsOf(id).scim.request<ScimEndpoint[]>('GET', '/endpoints'),
});

/**
 * Forget every environment session of this tab (keys `oph:t:*`): they belong to the operator of the hub session, so
 * they go when that session ends or another one starts.
 */
export function forgetTargetSessions(): void {
  for (const s of sessions.values()) s.clear();
  if (typeof sessionStorage !== 'undefined') {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith('oph:t:')) keys.push(k);
    }
    keys.forEach((k) => store.remove(k));
  }
  notify();
}

/**
 * Store the token pairs the hub sign-in returned, replacing every earlier environment session (a federated sign-in
 * returns none); environments that failed stay signed out.
 */
export function storeTargetLogins(list: TargetLogin[] | undefined): void {
  forgetTargetSessions();
  for (const t of list ?? []) {
    if (t.access_token) {
      targetSession(t.id).setTokens(t as TokenResponse);
      store.remove(`oph:t:${t.id}:error`);
    } else {
      targetSession(t.id).clear();
      store.set(`oph:t:${t.id}:error`, t.error || t.code || 'sign-in failed');
    }
  }
  notify();
}

/** Why sign-in to `id` failed at hub sign-in (or at its last federated sign-in), if it did. */
export const targetLoginError = (id: string): string | null => store.get(`oph:t:${id}:error`);

/** Remember why a federated sign-in to `id` failed (shown by its sign-in card). */
export function noteTargetLoginError(id: string, message: string): void {
  store.set(`oph:t:${id}:error`, message);
  notify();
}

/** Sign in to one environment again (failed at hub sign-in, or its session ended). */
export async function signInTarget(id: string, username: string, password: string): Promise<void> {
  const r = await hubFetch(`${BASE_URL}/auth-targets/${encodeURIComponent(id)}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const body = (await r.json().catch(() => ({}))) as TokenResponse & { error?: string };
  if (!r.ok || !body.access_token) throw new Error(body.error || `Sign-in failed (HTTP ${r.status})`);
  targetSession(id).setTokens(body);
  store.remove(`oph:t:${id}:error`);
  notify();
}

/** Sign in to one environment with the code of a federated sign-in at its AUTH (see `../sso.ts`). */
export async function signInTargetWithCode(
  id: string,
  body: { code: string; codeVerifier: string; redirectUri: string }
): Promise<void> {
  const r = await hubFetch(`${BASE_URL}/auth-targets/${encodeURIComponent(id)}/code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const res = (await r.json().catch(() => ({}))) as TokenResponse & { error?: string };
  if (!r.ok || !res.access_token) throw new Error(res.error || `Sign-in failed (HTTP ${r.status})`);
  targetSession(id).setTokens(res);
  store.remove(`oph:t:${id}:error`);
  notify();
}

/** Revoke and forget one environment's session. */
export async function signOutTarget(id: string): Promise<void> {
  if (sharesHubSession(id)) return; // the hub session's, ended by the hub sign-out
  const s = targetSession(id);
  const access = s.peek();
  const refresh = s.refreshToken();
  s.clear();
  notify();
  if (!access || !refresh) return;
  try {
    await hubFetch(`${BASE_URL}/auth-targets/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Target-Token': access },
      body: JSON.stringify({ refresh_token: refresh }),
    });
  } catch {
    /* best effort */
  }
}

/** At hub sign-out: revoke every environment session (needs the hub token, so before the hub session ends). */
export async function signOutAllTargets(): Promise<void> {
  await Promise.all(targetList().map((t) => signOutTarget(t.id)));
  for (const s of sessions.values()) s.clear();
  notify();
}

// ---------------------------------------------------------------------------------------- target list, active

let targets: AuthTargetInfo[] = [];
let targetsLoad: Promise<AuthTargetInfo[]> | null = null;
let active = '';

export const targetList = () => targets;

/** Production first, then the others in configured order (every multi-environment view lists them like this). */
export const byProductionFirst = <T extends { id: string }>(list: T[]): T[] =>
  [...list].sort((a, b) => Number(isProduction(b.id)) - Number(isProduction(a.id)));
const isProduction = (id: string) => /^prod/i.test(id);

export function loadTargets(): Promise<AuthTargetInfo[]> {
  targetsLoad ??= hubFetch(`${BASE_URL}/auth-targets`)
    .then((r) => (r.ok ? (r.json() as Promise<AuthTargetInfo[]>) : []))
    .catch(() => [])
    .then((list) => {
      targets = byProductionFirst(list);
      notify();
      return targets;
    });
  return targetsLoad;
}

/** Test hook: forget the cached list. */
export function resetTargets(): void {
  targets = [];
  targetsLoad = null;
  active = '';
  sessions.clear();
  views.clear();
  clients.clear();
}

/**
 * The target to show when the URL names none: the last one used while signed in, else the first signed-in one, else
 * the last one used, else the first.
 */
export function defaultTarget(): string {
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST);
  } catch {
    /* private mode */
  }
  const signedIn = (id: string) => targetSession(id).isSignedIn();
  return (
    targets.find((t) => t.id === last && signedIn(t.id))?.id ??
    targets.find((t) => signedIn(t.id))?.id ??
    targets.find((t) => t.id === last)?.id ??
    targets[0]?.id ??
    ''
  );
}

export function setActiveTarget(id: string): void {
  if (id === active) return;
  active = id;
  try {
    if (id) localStorage.setItem(LAST, id);
  } catch {
    /* private mode */
  }
  notify();
}

export const activeTarget = () => active;
export const activeTargetInfo = () => targets.find((t) => t.id === active);

// ------------------------------------------------------------------------------- what the ported pages import

/** The AUTH admin client of the active target (pages call it as auth-admin's module-level `client`). */
export const client: AuthClient = new Proxy({} as AuthClient, {
  get: (_, key) => (clientsOf(active).auth as unknown as Record<string | symbol, unknown>)[key],
});

export interface ScimEndpoint {
  tenantId: string;
  endpoint: string;
  target: 'nycelogic' | 'auth';
  trace: boolean;
}

/** SCIM gateway admin of the active target (`/api/scim/admin/endpoints`). */
export const scim = {
  list: () => clientsOf(active).scim.request<ScimEndpoint[]>('GET', '/endpoints'),
  get: (tenantId: string) =>
    clientsOf(active).scim.request<ScimEndpoint>('GET', `/endpoints/${encodeURIComponent(tenantId)}`),
  put: (tenantId: string, body: Omit<ScimEndpoint, 'tenantId'>) =>
    clientsOf(active).scim.request<ScimEndpoint>('PUT', `/endpoints/${encodeURIComponent(tenantId)}`, body),
  remove: (tenantId: string) =>
    clientsOf(active).scim.request<void>('DELETE', `/endpoints/${encodeURIComponent(tenantId)}`),
};

export interface Login {
  username: string;
  tenantId: string;
  /** The target token's claims; `canWrite` also needs the hub's `ophub:auth:write`. */
  access: Access;
}

/** Sign-in state of the active target, shaped like auth-admin's `useLogin()`. Null when not signed in there. */
export function useLogin(): Login | null {
  const hub = useHubLogin();
  useSyncExternalStore(subscribe, () => version);
  const token = active ? targetSession(active).peek() : null;
  if (!token) return null;
  const access = accessFromToken(token);
  const hubRead = hasScope(hub?.scopes, SCOPES.authRead);
  const hubWrite = hasScope(hub?.scopes, SCOPES.authWrite);
  return {
    username: hub?.username ?? access.subject,
    tenantId: access.tenantId ?? '',
    access: { ...access, canRead: access.canRead && hubRead, canWrite: access.canWrite && hubWrite },
  };
}

/** Re-render on target list / selection / session changes. */
export function useTargets(): {
  targets: AuthTargetInfo[];
  active: string;
  signedIn: (id: string) => boolean;
} {
  useSyncExternalStore(subscribe, () => version);
  return { targets, active, signedIn: (id) => targetSession(id).isSignedIn() };
}

/** Same page, same target: links keep `?target=`. */
export const identityPath = (path: string, target = active) =>
  `${path}${target ? `${path.includes('?') ? '&' : '?'}target=${encodeURIComponent(target)}` : ''}`;
export const tenantPath = (id: string) => identityPath(`/identity/tenants/${encodeURIComponent(id)}`);

// Access page helpers (auth-admin names).
export const CLIENT_VERSION = 'ops-hub';
export const apiBaseUrl = () => `${BASE_URL}/auth-admin/${active}`;
export const currentEnvironment = () => ({ id: active, label: activeTargetInfo()?.label ?? active });
/** On the Access page: end the session of the active environment only. */
export const logout = () => signOutTarget(active);
